import {
  MAABAR_AI_ENDPOINT,
  maabarAiHeaders,
  MAABAR_AI_PERSONA_NAME,
  MAABAR_AI_TASKS,
} from './config';

// Every web AI call goes to maabar-ai (Gemini) and nowhere else. Until Stage 2
// (2026-10-12) a failure here was retried through Ai-proxy with a client-built
// prompt; Ai-proxy has refused every prompt but the three calculator ones
// since v59, so that retry could only fail — and it replaced the real error
// (a 429, a 400) with its 403. The error now surfaces as maabar-ai sent it.
// Anthropic is not used anywhere, including as a fallback (owner, 2026-10-12).
export async function requestMaabarAI(task, payload, { fetchImpl = fetch, headers } = {}) {
  const response = await fetchImpl(MAABAR_AI_ENDPOINT, {
    method: 'POST',
    headers: headers || await maabarAiHeaders(),
    body: JSON.stringify({
      task,
      personaName: MAABAR_AI_PERSONA_NAME,
      payload,
    }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || data?.error) {
    const err = new Error(data?.error || `AI request failed with status ${response.status}`);
    err.status = response.status;
    throw err;
  }

  return data;
}

export async function generateIdeaToProductReport({
  language,
  mode,
  initialIdea,
  questions,
  answers,
}) {
  const data = await requestMaabarAI(MAABAR_AI_TASKS.IDEA_TO_PRODUCT, {
    language,
    mode,
    initialIdea,
    questions,
    answers,
  });

  return data.result;
}

export async function requestProductConversationReply({
  language,
  conversation,
  userMessage,
  userProfile,
}) {
  const data = await requestMaabarAI(MAABAR_AI_TASKS.PRODUCT_CONVERSATION, {
    language,
    conversation,
    userMessage,
    userProfile,
  });

  return data.result;
}

export async function translateChatMessage({
  text,
  sourceLanguage,
  targetLanguage,
  conversationRole = 'trade_chat',
}) {
  const data = await requestMaabarAI(MAABAR_AI_TASKS.CHAT_TRANSLATION, {
    text,
    sourceLanguage,
    targetLanguage,
    conversationRole,
  });

  return data.translatedText || '';
}

// Suggest up to 3 HS (Harmonized System) codes for a product.
// Returns { items: [{ code, description }], disclaimer } — items is [] on error.
// The disclaimer is the server's (maabar-ai/hsCode.ts, in the requested
// language); HS_SUGGESTION_DISCLAIMER in src/lib/hsCodeDisclaimer.js is the
// local copy the UI falls back to, so the notice never depends on the reply.
export async function suggestHsCodes({
  productName,
  category,
  description,
  language = 'en',
}) {
  if (!productName) return { items: [], disclaimer: '' };
  try {
    const data = await requestMaabarAI(MAABAR_AI_TASKS.HS_CODE_SUGGEST, {
      productName,
      category,
      description,
      language,
    });
    const list = Array.isArray(data?.result?.suggestions) ? data.result.suggestions : [];
    const items = list
      .filter(item => item && typeof item.code === 'string')
      .slice(0, 3)
      .map(item => ({
        code: String(item.code).trim(),
        description: String(item.description || '').trim(),
      }));
    const disclaimer = typeof data?.result?.disclaimer === 'string' ? data.result.disclaimer.trim() : '';
    return { items, disclaimer };
  } catch (err) {
    console.error('[suggestHsCodes] error:', err?.message || err);
    return { items: [], disclaimer: '' };
  }
}

export async function requestSupportReply({
  language,
  conversation,
  userMessage,
  userProfile,
}) {
  const data = await requestMaabarAI(MAABAR_AI_TASKS.CUSTOMER_SUPPORT, {
    language,
    conversation,
    userMessage,
    userProfile,
  });

  return data.result;
}
